import { useCallback, useEffect, useMemo, useState } from 'react';
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import { uniqBy, isEmpty } from 'mojito/utils';
import { useMarketGroups, useMarketIdsFromMarketInfos } from 'modules/common/hooks';

const { utils: MarketGroupUtils, types: MarketGroupTypes } =
    MojitoServices.SportsContent.MarketGroups;
const SortingHelper = MojitoPresentation.Utils.SortingHelper;
const { useAppContext, useInstanceId } = MojitoCore.Presentation.Hooks;

const { ALL_MARKETS } = MarketGroupTypes.GROUP_TYPES;

/**
 * Event list module related hooks.
 *
 * @name EventMarketsHooks
 * @memberof Mojito.Modules
 */

/**
 * Hook fetches aggregated market infos list for specified market group ids.
 * Useful to display list of collapsed markets within selected markets group.
 * Each market info object contains markets list where each market has market id (lineId) which is used to load actual markets once market component is expanded.
 *
 * @function useAggregatedMarketInfos
 *
 * @param {Array<string>} marketGroupIds - The list of market group ids.
 * @param {boolean} [shouldRequest = true] - Flag indicating if hook should perform market groups request.
 *
 * @returns {Array<Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo>} Market infos. Presents list of aggregated market descriptors.
 * @memberof Mojito.Modules.EventMarketsHooks
 */
export const useAggregatedMarketInfos = (marketGroupIds, shouldRequest = true) => {
    const instanceId = useInstanceId();
    const marketGroups = useMarketGroups(marketGroupIds, instanceId, shouldRequest);
    const { language } = useAppContext().systemSettings();

    return useMemo(() => {
        if (!marketGroups) {
            return;
        }
        const marketsComparator = (i1, i2) => SortingHelper.byDisplayOrderAndName(i1, i2, language);
        let marketInfos = Object.values(marketGroups).flatMap(group => group.marketInfos);
        // Potentially same aggregated market info can be present in several groups, you know.
        marketInfos = uniqBy(marketInfos, info => info.id);
        return marketInfos && [...marketInfos].sort(marketsComparator);
    }, [marketGroups, language]);
};

/**
 * Hook resolves game line aggregated market info from event and adds it in front of marketInfos list.
 * Note: the hook does not mutate marketInfos array.
 *
 * @function useGameLineAssurance
 *
 * @param {Array<Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo>} marketInfos - List of market infos.
 * @param {object} [eventItem] - Event item object. If not provided then marketInfos will be returned with no changes.
 * @param {boolean} [checkOwns = false] - If true then hook will check if game lime markets are actually part of provided market infos and only in that case will attach game line.
 *
 * @returns {Array<Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo>|undefined} Market infos list which may include game line info as its first element.
 * @memberof Mojito.Modules.EventMarketsHooks
 */
export const useGameLineAssurance = (marketInfos, eventItem, checkOwns = false) => {
    const allMarketIds = useMarketIdsFromMarketInfos(marketInfos);

    return useMemo(() => {
        if (!eventItem) {
            return marketInfos;
        }
        const { gameLine, marketLines = {} } = eventItem;
        const gameLineMarketIds = MarketGroupUtils.getMarketIdsByMarketOption(
            marketLines,
            gameLine?.options
        );
        const isGameLineInGroup = gameLineMarketIds.some(gameLineMarketId =>
            allMarketIds.includes(gameLineMarketId)
        );

        const gameLineInfo =
            !checkOwns || isGameLineInGroup
                ? MarketGroupUtils.createGameLineMarketInfo(marketLines, gameLine)
                : undefined;
        if (gameLineInfo) {
            gameLineInfo.earlyPayout = MarketGroupUtils.createGameLineEarlyPayout(
                marketInfos,
                gameLineMarketIds
            );
        }
        return gameLineInfo
            ? [gameLineInfo, ...omitGameLineMarkets(marketInfos, gameLineInfo)]
            : marketInfos;
    }, [eventItem, marketInfos, checkOwns, allMarketIds]);
};

/*
 * Hook allows to handle item selection state.
 * Use setSelectedId callback to change selection.
 * If none selected the default will be returned based on item.default property or the first one from the list.
 *
 * function useItemsSelector
 *
 * @param {Array<object>} [items = []] - List of items.
 * @param {Array} [deps = []] - List of dependencies will be used to reset active item id to the default one.
 *
 * @returns {Array} Array with two items, first is currently selected item id and second if setter callback function to change selected item.
 * @memberof Mojito.Modules.EventMarketsHooks
 */
export const useItemsSelector = (items = [], deps = []) => {
    const [selectedId, setSelectedId] = useState();
    const isValidSelection = items.some(item => item.id === selectedId);
    const getDefaultItemId = items => items.find(items => items.default)?.id;

    const activeItemId = isValidSelection ? selectedId : getDefaultItemId(items) || items[0]?.id;
    useEffect(
        () => setSelectedId(undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        deps
    );
    useEffect(() => {
        if (!isValidSelection) {
            setSelectedId(undefined);
        }
    }, [isValidSelection]);

    return [activeItemId, setSelectedId];
};

/**
 * Hook resolves market group switcher settings.
 *
 * @function useMarketGroupSwitcher
 *
 * @param {object} eventItem - Event item object.
 * @param {boolean} [showAllGroups = false] - True if switcher should contain all available market group ids.
 *
 * @returns {Array} First element in array is an object that defines current state of a switcher, second is a callback of type {@link Mojito.Modules.EventMarketsGroupSwitcher.OnGroupsChange|OnGroupsChange} to be executed on group switch.
 *
 * @memberof Mojito.Modules.EventMarketsHooks
 */
export const useMarketGroupSwitcher = (eventItem, showAllGroups = false) => {
    const [selectedSwitcherInfo, setSelectedSwitcherInfo] = useState({});
    const activeSwitcherInfo = showAllGroups
        ? getCompositeMarketGroupInfo(eventItem)
        : selectedSwitcherInfo;

    const onMarketGroupsChange = useCallback(payload => {
        setSelectedSwitcherInfo(payload);
    }, []);

    return [activeSwitcherInfo, onMarketGroupsChange];
};

/**
 * Hook detects the group that will be selected by default for a user. It sets `default` flag to that market group.
 * It also sets `top` flag to that group that is supposed to be the group of top interset. This is the first group from the list.
 *
 * @function useDefaultMarketGroup
 *
 * @param {Array<object>} marketGroups - List of market groups.
 * @param {object} eventItem - Event item object.
 * @param {object} config - Application configuration object.
 *
 * @returns {Array} Array of market groups with default value set to the one that matches provided groupId.
 * @memberof Mojito.Modules.EventMarketsHooks
 */
export const useDefaultMarketGroup = (marketGroups, eventItem, config) => {
    const marketsDefaultThreshold = config.marketThreshold.default;
    const marketsMinThreshold =
        (marketsDefaultThreshold > 0 && config.marketThreshold[eventItem.sportId]) ||
        marketsDefaultThreshold;
    const topMarketGroup = marketGroups[0];
    const allMarketsGroup = findByType(ALL_MARKETS, marketGroups) || {};
    const insufficientMarketsCountInTopGroup = topMarketGroup?.marketsCount < marketsMinThreshold;
    // In case there are not enough markets in a top market group, we are switching to "All Markets" group to make sure user has
    // enough markets to bet on a screen
    const defaultMarketGroup =
        insufficientMarketsCountInTopGroup && !isEmpty(allMarketsGroup)
            ? allMarketsGroup
            : topMarketGroup;
    return useMemo(() => {
        return marketGroups.map(item => {
            const isTopGroup = topMarketGroup.id === item.id;
            const isDefaultGroup = defaultMarketGroup.id === item.id;
            return { ...item, default: isDefaultGroup, top: isTopGroup };
        });
    }, [marketGroups, defaultMarketGroup?.id, topMarketGroup?.id]);
};

/**
 * Hook resolve market group ids from the list of provided market groups.
 *
 * @function useGroupIds
 *
 * @param {Array<object>} groups - List of market groups.
 *
 * @returns {Array} Array of market group ids.
 * @memberof Mojito.Modules.EventMarketsHooks
 */
export const useGroupIds = groups => {
    return useMemo(() => (groups || []).map(group => group.id), [groups]);
};

const findByType = (type, items = []) => items?.find(item => item.type === type);

const getCompositeMarketGroupInfo = (eventItem = {}) => {
    const { marketGroups = [] } = eventItem;
    return {
        groups: marketGroups,
        isDefault: true,
        isComposite: false,
    };
};

/*
 * The function will remove game line market ids from each market info that contains them.
 * If market info becomes empty after this process (no marketIds available any more) then the whole market info object will be omitted from the list.
 * This is need to make sure that market group doesn't duplicate markets that are already part of the game line which is, typically, rendered on top of the markets group.
 *
 * function omitGameLineMarkets
 * @private
 *
 * @param {Array<Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo>} marketInfos - List of aggregated market infos.
 * @param {Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo} gameLineInfo - Game line aggregated market info.
 *
 * @returns {Array<Mojito.Services.SportsContent.MarketGroups.types.AggregatedMarketInfo>} Array of sanitized market infos.
 * @memberof Mojito.Modules.EventMarketsHooks
 */
const omitGameLineMarkets = (marketInfos, gameLineInfo) => {
    const gameLineMarketIds = gameLineInfo.markets.map(market => market?.id).filter(Boolean);
    return (marketInfos || [])
        .map(info => {
            return {
                ...info,
                markets: info.markets.filter(market => !gameLineMarketIds.includes(market?.id)),
            };
        })
        .filter(info => info.markets.length > 0);
};
